import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { marked } from 'marked';
import { useAppStore } from '../stores/app-store';
import { api } from '../lib/api';
import {
  Upload,
  FileText,
  FileCode,
  File,
  FileImage,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Image as ImageIcon,
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  BookOpen,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTitle,
} from './ui/responsive-modal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import type { SectionType } from '../types';
import { parseDocx } from '../utils/parse-docx';
import { parsePdf } from '../utils/parse-pdf';

interface ImportBookModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ImportFormat = 'txt' | 'md' | 'docx' | 'pdf';

interface ParsedChapter {
  title: string;
  content: string;
  sectionType: SectionType;
}

export function ImportBookModal({ open, onOpenChange }: ImportBookModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createProject = useAppStore((s) => s.createProject);
  const updateProject = useAppStore((s) => s.updateProject);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ParsedChapter[] | null>(null);
  const [coverImage, setCoverImage] = useState<string | undefined>();
  const [step, setStep] = useState<'upload' | 'preview' | 'details'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Localized supported-format cards — depends on `t`, so it lives inside the component.
  const formatTypes = [
    { ext: '.docx', icon: FileText, label: 'Word', color: 'from-blue-500 to-blue-600' },
    { ext: '.pdf', icon: FileImage, label: 'PDF', color: 'from-red-500 to-red-600' },
    { ext: '.txt', icon: File, label: t('importBook.formatText', 'Texto'), color: 'from-slate-500 to-slate-600' },
    { ext: '.md', icon: FileCode, label: 'Markdown', color: 'from-teal-500 to-teal-600' },
  ];

  const detectFormat = (filename: string): ImportFormat | null => {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'txt') return 'txt';
    if (ext === 'md' || ext === 'markdown') return 'md';
    if (ext === 'docx') return 'docx';
    if (ext === 'pdf') return 'pdf';
    return null;
  };

  // Configure marked for proper HTML output
  const parseMarkdown = (markdown: string): string => {
    // Configure marked options
    marked.setOptions({
      breaks: true, // Convert \n to <br>
      gfm: true, // GitHub Flavored Markdown
    });
    return marked.parse(markdown, { async: false }) as string;
  };

  const parseTextContent = useCallback((text: string): ParsedChapter[] => {
    const chapters: ParsedChapter[] = [];

    // Only # (h1) are chapters, ## and below are subheadings within chapters
    const h1Matches = text.match(/^#\s+.+$/gm);

    if (h1Matches && h1Matches.length > 0) {
      // Split by h1 only (single #)
      const sections = text.split(/^(?=#\s+)/m).filter(s => s.trim());
      sections.forEach((section, index) => {
        const lines = section.trim().split('\n');
        const titleLine = lines[0];
        const content = lines.slice(1).join('\n').trim();
        const titleMatch = titleLine.match(/^#\s+(.+)$/);
        // Also parse the title for any markdown formatting
        const rawTitle = titleMatch ? titleMatch[1].trim() : t('importBook.chapterFallbackTitle', 'Capítulo {{number}}', { number: index + 1 });
        const chapterTitle = rawTitle.replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1');
        const sectionType = detectSectionType(chapterTitle);

        // Use marked to convert the content to HTML
        const htmlContent = parseMarkdown(content);

        chapters.push({
          title: chapterTitle,
          content: htmlContent,
          sectionType,
        });
      });
    } else {
      // Try Chapter pattern
      const chapterMatches = text.match(/^Chapter\s+(\d+|[IVXLCDM]+)[:\s]*(.*)/gim);
      if (chapterMatches && chapterMatches.length > 0) {
        const sections = text.split(/^(?=Chapter\s+\d+|Chapter\s+[IVXLCDM]+)/gim).filter(s => s.trim());
        sections.forEach((section, index) => {
          const lines = section.trim().split('\n');
          const titleLine = lines[0];
          const content = lines.slice(1).join('\n').trim();
          const htmlContent = parseMarkdown(content);
          chapters.push({
            title: titleLine.trim() || t('importBook.chapterFallbackTitle', 'Capítulo {{number}}', { number: index + 1 }),
            content: htmlContent,
            sectionType: 'chapter',
          });
        });
      } else {
        // Single chapter with all content - convert markdown with marked
        const htmlContent = parseMarkdown(text);
        chapters.push({
          title: t('importBook.chapterFallbackTitle', 'Capítulo {{number}}', { number: 1 }),
          content: htmlContent,
          sectionType: 'chapter',
        });
      }
    }
    return chapters;
  }, [t]);

  const detectSectionType = (title: string): SectionType => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('dedication')) return 'dedication';
    if (lowerTitle.includes('copyright')) return 'copyright';
    if (lowerTitle.includes('prologue')) return 'prologue';
    if (lowerTitle.includes('epilogue')) return 'epilogue';
    if (lowerTitle.includes('foreword')) return 'foreword';
    if (lowerTitle.includes('preface')) return 'preface';
    if (lowerTitle.includes('introduction')) return 'introduction';
    if (lowerTitle.includes('acknowledgment')) return 'acknowledgments';
    if (lowerTitle.includes('about the author') || lowerTitle.includes('about author')) return 'about-author';
    if (lowerTitle.includes('glossary')) return 'glossary';
    if (lowerTitle.includes('bibliography') || lowerTitle.includes('references')) return 'bibliography';
    if (lowerTitle.includes('appendix')) return 'appendix';
    if (lowerTitle.includes('afterword')) return 'afterword';
    if (lowerTitle.includes('table of contents') || lowerTitle === 'contents') return 'toc';
    if (lowerTitle.includes('also by') || lowerTitle.includes('other books')) return 'also-by';
    if (lowerTitle.match(/^part\s+/i)) return 'part';
    return 'chapter';
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const format = detectFormat(selectedFile.name);
    if (!format) {
      setError(t('importBook.unsupportedFormat', 'Formato não suportado. Use arquivos .txt, .md, .docx ou .pdf'));
      return;
    }

    setFile(selectedFile);
    setError(null);
    setCoverImage(undefined);
    setParsing(true);

    const fileName = selectedFile.name.replace(/\.[^/.]+$/, '');
    if (!title) {
      setTitle(fileName.replace(/[-_]/g, ' '));
    }

    try {
      if (format === 'txt' || format === 'md') {
        const text = await selectedFile.text();
        const parsed = parseTextContent(text);
        setPreview(parsed);
      } else if (format === 'docx') {
        const result = await parseDocx(selectedFile);
        const chapters = result.chapters.map(ch => ({
          title: ch.title,
          content: ch.content,
          sectionType: detectSectionType(ch.title),
        }));
        setPreview(chapters);
        if (result.coverImage) setCoverImage(result.coverImage);
        if (result.title && !title) setTitle(result.title);
        if (result.author && !author) setAuthor(result.author);
      } else if (format === 'pdf') {
        const result = await parsePdf(selectedFile);
        const chapters = result.chapters.map(ch => ({
          title: ch.title,
          content: ch.content,
          sectionType: detectSectionType(ch.title),
        }));
        setPreview(chapters);
        if (result.coverImage) setCoverImage(result.coverImage);
        if (result.title && !title) setTitle(result.title);
        if (result.author && !author) setAuthor(result.author);
      }
      setStep('preview');
    } catch (err) {
      console.error('Parse error:', err);
      setError(t('importBook.parseError', 'Falha ao processar arquivo. Verifique o formato.'));
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const input = fileInputRef.current;
      if (input) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(droppedFile);
        input.files = dataTransfer.files;
        handleFileSelect({ target: input } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleImport = async () => {
    if (!file || !preview) return;

    setImporting(true);
    setError(null);

    try {
      const projectId = await createProject({
        title: title || 'Imported Book',
        author: author || '',
        coverImage: coverImage,
      }, 'blank');

      const currentProjects = useAppStore.getState().projects;
      const project = currentProjects.find(p => p.id === projectId);
      if (!project) throw new Error('Failed to create project');

      if (project.chapters.length > 0) {
        for (const ch of project.chapters) {
          await api.deleteChapter(ch.id);
        }
      }

      for (let i = 0; i < preview.length; i++) {
        const ch = preview[i];
        await api.createChapter({
          projectId,
          title: ch.title,
          sectionType: ch.sectionType,
          content: ch.content,
        });
      }

      await useAppStore.getState().fetchProjects();

      if (coverImage) {
        await updateProject(projectId, {
          metadata: { ...project.metadata, coverImage }
        });
      }

      onOpenChange(false);
      // cerimônia: o manuscrito chegou (silencia o "livro nasce" deste projeto)
      localStorage.setItem(`moment-born-${projectId}`, '1');
      window.dispatchEvent(new CustomEvent('reformu:moment', {
        detail: { type: 'arrived', title: project.metadata.title || '' },
      }));
      navigate(`/app/editor/${projectId}`);
    } catch (err) {
      console.error('Import error:', err);
      setError(t('importBook.importError', 'Falha ao importar livro. Tente novamente.'));
    } finally {
      setImporting(false);
    }
  };

  const countWords = (html: string): number => {
    const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text ? text.split(' ').length : 0;
  };

  const totalWords = preview?.reduce((sum, ch) => sum + countWords(ch.content), 0) || 0;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange}>
      <ResponsiveModalContent className="gap-0 overflow-hidden p-0 sm:max-w-4xl">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-blue-500/5 via-blue-500/10 to-transparent p-6 pb-4">
          <div>
            <ResponsiveModalTitle className="text-xl font-semibold text-foreground">
              {t('importBook.title', 'Importar Livro')}
            </ResponsiveModalTitle>
            <p className="text-sm text-muted-foreground mt-0.5">
              {step === 'upload' && t('importBook.stepUploadHint', 'Selecione um arquivo para importar')}
              {step === 'preview' && t('importBook.stepPreviewHint', 'Revise a estrutura detectada')}
              {step === 'details' && t('importBook.stepDetailsHint', 'Confirme os detalhes do livro')}
            </p>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-3 mt-4">
            {[
              { key: 'upload', label: t('importBook.stepFile', 'Arquivo') },
              { key: 'preview', label: t('importBook.stepStructure', 'Estrutura') },
              { key: 'details', label: t('importBook.stepDetails', 'Detalhes') },
            ].map((s, i, arr) => {
              const stepIndex = arr.findIndex(x => x.key === step);
              const isActive = step === s.key;
              const isCompleted = stepIndex > i;

              return (
                <div key={s.key} className="flex items-center">
                  <div className={cn(
                    "flex items-center gap-2 text-sm font-medium transition-colors",
                    isActive ? "text-blue-500" : isCompleted ? "text-blue-500/60" : "text-muted-foreground"
                  )}>
                    <span className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold",
                      isActive
                        ? "bg-blue-500 text-white"
                        : isCompleted
                          ? "bg-blue-500/20 text-blue-500"
                          : "bg-muted text-muted-foreground"
                    )}>
                      {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                    </span>
                    <span>{s.label}</span>
                  </div>
                  {i < arr.length - 1 && <div className="w-8 h-px bg-border ml-3" />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 min-h-[400px] max-h-[65vh] overflow-auto">
          <AnimatePresence mode="wait">
            {step === 'upload' && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {/* Supported Formats */}
                <div className="grid grid-cols-4 gap-3">
                  {formatTypes.map(({ ext, icon: Icon, label, color }) => (
                    <div key={ext} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-muted/50">
                      <div className={cn('p-2 rounded-lg bg-gradient-to-br', color)}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-medium text-foreground">{label}</span>
                        <span className="block text-xs text-muted-foreground">{ext}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* File Upload */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                  className={cn(
                    'relative border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all',
                    parsing
                      ? 'border-blue-500 bg-blue-500/5'
                      : 'border-border hover:border-blue-500/50 hover:bg-muted/50'
                  )}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.md,.markdown,.docx,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {parsing ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />
                      <div>
                        <p className="font-medium text-foreground">{t('importBook.processingFile', 'Processando arquivo...')}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('importBook.extractingChaptersAndCover', 'Extraindo capítulos e capa')}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 rounded-2xl bg-muted">
                        <Upload className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          {t('importBook.dragOrClickToSelect', 'Arraste um arquivo ou clique para selecionar')}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('importBook.supportedFormats', 'Suporta DOCX, PDF, TXT e Markdown')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}
              </motion.div>
            )}

            {step === 'preview' && preview && (
              <motion.div
                key="preview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-4"
              >
                {/* Top bar with file info and cover */}
                <div className="grid md:grid-cols-2 gap-4">
                  {/* File info */}
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-[rgba(94,234,212,0.1)] border border-[rgba(94,234,212,0.25)]">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[color:var(--aurora)]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{file?.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {((file?.size || 0) / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setFile(null);
                        setPreview(null);
                        setStep('upload');
                      }}
                      className="p-1.5 rounded-lg hover:bg-background/80 transition-colors"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Cover preview */}
                  {coverImage && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <div className="relative group flex-shrink-0">
                        <img
                          src={coverImage}
                          alt={t('importBook.extractedCoverAlt', 'Capa extraída')}
                          className="w-12 h-16 object-cover rounded-lg shadow"
                        />
                        <button
                          onClick={() => setCoverImage(undefined)}
                          className="absolute -top-1.5 -right-1.5 p-0.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                          <ImageIcon className="h-4 w-4 text-[color:var(--aurora)]" />
                          {t('importBook.coverDetected', 'Capa Detectada')}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {t('importBook.extractedFromDocument', 'Extraída do documento')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Chapters preview */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-foreground">{t('importBook.structureDetected', 'Estrutura Detectada')}</h3>
                    <span className="text-xs text-muted-foreground">
                      {t('importBook.sectionsAndWords', '{{sections}} seções • {{words}} palavras', { sections: preview.length, words: totalWords.toLocaleString() })}
                    </span>
                  </div>
                  <div className="max-h-72 overflow-auto rounded-xl border">
                    {preview.map((ch, index) => (
                      <div
                        key={index}
                        className={cn(
                          'flex items-center gap-3 p-3 transition-colors hover:bg-muted/50',
                          index > 0 && 'border-t'
                        )}
                      >
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-foreground truncate">{ch.title}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {ch.sectionType.replace('-', ' ')}
                          </p>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {t('importBook.wordsCount', '{{amount}} palavras', { amount: countWords(ch.content).toLocaleString() })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {step === 'details' && (
              <motion.div
                key="details"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
                className="space-y-6"
              >
                {/* Two column layout */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left column - Summary card */}
                  <div className="flex flex-col items-center justify-center gap-4 p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-purple-500/10">
                    {coverImage ? (
                      <img src={coverImage} alt={t('importBook.coverAlt', 'Capa')} className="w-24 h-36 object-cover rounded-lg shadow-lg" />
                    ) : (
                      <div className="w-24 h-36 rounded-lg bg-muted flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    <div className="text-center">
                      <p className="font-semibold text-foreground truncate max-w-full">{file?.name}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('importBook.sectionsAndWords', '{{sections}} seções • {{words}} palavras', { sections: preview?.length ?? 0, words: totalWords.toLocaleString() })}
                      </p>
                    </div>
                  </div>

                  {/* Right column - Form fields */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label htmlFor="import-title" className="text-sm font-medium text-foreground">
                        {t('importBook.bookTitleLabel', 'Título do Livro')}
                      </label>
                      <Input
                        id="import-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder={t('importBook.bookTitlePlaceholder', 'Título do livro')}
                        className="h-11"
                        autoFocus
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="import-author" className="text-sm font-medium text-foreground">
                        {t('importBook.authorNameLabel', 'Nome do Autor')}
                      </label>
                      <Input
                        id="import-author"
                        value={author}
                        onChange={(e) => setAuthor(e.target.value)}
                        placeholder={t('importBook.authorNamePlaceholder', 'Seu nome')}
                        className="h-11"
                      />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-xl">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-muted/30">
          {step === 'upload' ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                {t('importBook.cancel', 'Cancelar')}
              </Button>
              <div />
            </>
          ) : step === 'preview' ? (
            <>
              <Button variant="ghost" onClick={() => setStep('upload')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t('importBook.back', 'Voltar')}
              </Button>
              <Button onClick={() => setStep('details')} className="gap-2">
                {t('importBook.continue', 'Continuar')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep('preview')} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                {t('importBook.back', 'Voltar')}
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing}
                className="gap-2 min-w-32"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('importBook.importing', 'Importando...')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    {t('importBook.importBookButton', 'Importar Livro')}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
